import {
    onboardingSubscriptionPlansResponseSchema,
    type OnboardingSubscriptionPlan,
} from '@houzy/schemas/partner-cockpit/onboarding';
import {
    partnerCockpitBillingPortalSchema,
    partnerCockpitSubscriptionViewSchema,
    type CancelPartnerCockpitSubscriptionInput,
    type PartnerCockpitBillingPortal,
    type PartnerCockpitSubscriptionView,
} from '@houzy/schemas/partner-cockpit/subscription';
import {
    billingPeriodDocumentLinkSchema,
    type BillingPeriodDocumentLink,
} from '@houzy/schemas/partner-management/partner-subscription-management';
import { queryOptions, useMutation, useQuery, useQueryClient, type UseQueryResult } from '@tanstack/react-query';

import { apiRequest, retryQueryUnlessClientError, type ApiContractError, type ApiError } from '../../../lib/api';
import { partnerCockpitApiPath } from '../../../lib/config';
import { STALE_TIME_MS } from '../../../lib/queryClient';

export const subscriptionQueryKey = ['subscription'] as const;

const path = (suffix = ''): string => partnerCockpitApiPath(`subscription${suffix}`);

export const subscriptionQueryOptions = queryOptions({
    queryKey: subscriptionQueryKey,
    queryFn: ({ signal }): Promise<PartnerCockpitSubscriptionView> =>
        apiRequest(path(), { signal, responseSchema: partnerCockpitSubscriptionViewSchema }),
    staleTime: STALE_TIME_MS,
    retry: retryQueryUnlessClientError,
});

export function useSubscription(): UseQueryResult<PartnerCockpitSubscriptionView> {
    return useQuery(subscriptionQueryOptions);
}

export const subscriptionPlansQueryOptions = queryOptions({
    queryKey: ['subscriptionPlans'],
    queryFn: async ({ signal }): Promise<OnboardingSubscriptionPlan[]> => {
        const response = await apiRequest(path('/plans'), {
            signal,
            responseSchema: onboardingSubscriptionPlansResponseSchema,
        });
        return response.plans;
    },
    staleTime: STALE_TIME_MS,
    retry: retryQueryUnlessClientError,
});

export function useSubscriptionPlans(): UseQueryResult<OnboardingSubscriptionPlan[]> {
    return useQuery(subscriptionPlansQueryOptions);
}

type SubscriptionMutation<TInput> = ReturnType<
    typeof useMutation<PartnerCockpitSubscriptionView, ApiError | ApiContractError, TInput>
>;

function useSubscriptionMutation<TInput>(
    request: (input: TInput) => Promise<PartnerCockpitSubscriptionView>,
): SubscriptionMutation<TInput> {
    const queryClient = useQueryClient();
    return useMutation<PartnerCockpitSubscriptionView, ApiError | ApiContractError, TInput>({
        mutationKey: [...subscriptionQueryKey, 'mutate'],
        mutationFn: request,
        onSuccess: (view: PartnerCockpitSubscriptionView) => {
            queryClient.setQueryData(subscriptionQueryKey, view);
        },
    });
}

export function useCancelSubscriptionMutation(): SubscriptionMutation<CancelPartnerCockpitSubscriptionInput> {
    return useSubscriptionMutation<CancelPartnerCockpitSubscriptionInput>((body) =>
        apiRequest(path('/cancellation'), {
            method: 'POST',
            body,
            responseSchema: partnerCockpitSubscriptionViewSchema,
        }),
    );
}

export function useRevokeCancellationMutation(): SubscriptionMutation<void> {
    return useSubscriptionMutation<void>(() =>
        apiRequest(path('/cancellation'), { method: 'DELETE', responseSchema: partnerCockpitSubscriptionViewSchema }),
    );
}

export function useBillingPortalMutation(): ReturnType<
    typeof useMutation<PartnerCockpitBillingPortal, ApiError | ApiContractError, void>
> {
    return useMutation<PartnerCockpitBillingPortal, ApiError | ApiContractError, void>({
        mutationKey: [...subscriptionQueryKey, 'billing-portal'],
        mutationFn: () =>
            apiRequest(path('/billing-portal'), { method: 'POST', responseSchema: partnerCockpitBillingPortalSchema }),
    });
}

export function useChargeDocumentMutation(): ReturnType<
    typeof useMutation<BillingPeriodDocumentLink, ApiError | ApiContractError, string>
> {
    return useMutation<BillingPeriodDocumentLink, ApiError | ApiContractError, string>({
        mutationKey: [...subscriptionQueryKey, 'charge-document'],
        mutationFn: (chargeUuid) =>
            apiRequest(path(`/charges/${chargeUuid}/document`), {
                responseSchema: billingPeriodDocumentLinkSchema,
            }),
    });
}
